import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useHistory} from 'react-router-dom';

import {
  apiCreateFdApplicationAgreement,
  apiGetFdAgreementTerms,
  apiGetFdApplicationAgreementsByApplicationId,
  apiUpdateFdApplicationProgress,
  apiUploadFile,
} from 'constants/Api';
import {EApplicationStateType} from 'constants/index';
import type {
  IFdAgreementDocType,
  IFdApplicationAgreementType,
  IFdApplicationType,
} from 'constants/interfaces';

import ApplicationJourneyStepLayout from './ApplicationJourneyStepLayout';
import {ApplicationApprovedCashflowReviewDialog} from './components/ApplicationApprovedCashflowCard';
import {
  ErrorBanner,
  FormField,
  StatusMessage,
  TextInput,
} from './shared/components';
import {useSupplierApplicationJourney} from './SupplierApplicationJourneyContext';

const AGREEMENT_PROGRESS_STEP = 8;

function lshEscapeForSvg(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function lshBuildSignatureFile(signatureName: string, agreementType: string) {
  const safeName = lshEscapeForSvg(signatureName.trim());
  const safeType = lshEscapeForSvg(agreementType);
  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="400" viewBox="0 0 1200 400">
  <rect width="100%" height="100%" fill="#ffffff" />
  <text x="80" y="220" font-family="cursive" font-size="72" fill="#0f172a">${safeName}</text>
  <text x="80" y="300" font-family="sans-serif" font-size="28" fill="#64748b">Agreement: ${safeType}</text>
</svg>`;

  return new File([svg], `agreement-signature-${Date.now()}.svg`, {
    type: 'image/svg+xml',
  });
}

function lshGetErrorMessage(error: any, fallback: string) {
  if (Array.isArray(error) && error.length > 0) {
    return String(error[0]);
  }

  return error?.message || fallback;
}

function lshGetRedirectPath(origin: string) {
  return origin === 'applications' ? '/applications' : '/customers';
}

const ApplicationAgreementStep: React.FC = () => {
  const history = useHistory();
  const {
    application,
    applicationId,
    origin,
    goPrevious,
    refreshJourney,
    setApplication,
  } = useSupplierApplicationJourney();

  const currentApplication = application as IFdApplicationType | undefined;
  const numericApplicationId = Number(
    currentApplication?.id || applicationId || 0,
  );
  const applicationIdValid =
    Number.isFinite(numericApplicationId) && numericApplicationId > 0;

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSigningId, setIsSigningId] = useState<number | null>(null);
  const [signatureName, setSignatureName] = useState('');
  const [terms, setTerms] = useState<IFdAgreementDocType[]>([]);
  const [signed, setSigned] = useState<IFdApplicationAgreementType[]>([]);
  const [acceptedTermIds, setAcceptedTermIds] = useState<number[]>([]);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [cashflowDialogOpen, setCashflowDialogOpen] = useState(false);

  const signedTermIds = useMemo(
    () => new Set(signed.map((item) => Number(item?.agreementDoc?.id))),
    [signed],
  );

  const allTermsSigned =
    terms.length > 0 &&
    terms.every((term) => signedTermIds.has(Number(term.id)));

  const loadAgreementData = useCallback(async () => {
    if (!applicationIdValid) return;

    try {
      setIsLoading(true);
      setErrorMessage(null);

      const [termResponse, signedResponse] = await Promise.all([
        apiGetFdAgreementTerms(),
        apiGetFdApplicationAgreementsByApplicationId(numericApplicationId),
      ]);

      setTerms(Array.isArray(termResponse) ? termResponse : []);
      setSigned(Array.isArray(signedResponse) ? signedResponse : []);
    } catch (error: any) {
      setErrorMessage(
        lshGetErrorMessage(
          error,
          "We couldn't load agreements. Please refresh and try again.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  }, [applicationIdValid, numericApplicationId]);

  useEffect(() => {
    void loadAgreementData();
  }, [loadAgreementData]);

  const toggleAccepted = useCallback((termId: number) => {
    setAcceptedTermIds((current) => {
      if (current.includes(termId)) {
        return current.filter((id) => id !== termId);
      }

      return [...current, termId];
    });
  }, []);

  const handleSignAgreement = useCallback(
    async (term: IFdAgreementDocType) => {
      if (!applicationIdValid || isSubmitting) return;

      const termId = Number(term.id);

      if (!acceptedTermIds.includes(termId)) {
        setErrorMessage(
          'Please accept the declaration checkbox before signing.',
        );
        return;
      }

      if (!signatureName.trim()) {
        setErrorMessage('Enter your signature name before signing agreements.');
        return;
      }

      try {
        setErrorMessage(null);
        setStatusMessage('');
        setIsSigningId(termId);

        const signatureFile = lshBuildSignatureFile(
          signatureName,
          term.agreementType,
        );
        const signatureUrl = await apiUploadFile(
          signatureFile,
          `fd-application-agreements/${numericApplicationId}_${termId}_${Date.now()}.svg`,
        );

        await apiCreateFdApplicationAgreement({
          applicationId: numericApplicationId,
          agreementDocId: termId,
          signature: signatureUrl,
        });

        setStatusMessage(`${term.agreementType} signed successfully.`);
        await loadAgreementData();
      } catch (error: any) {
        setErrorMessage(
          lshGetErrorMessage(
            error,
            "We couldn't sign this agreement. Please try again.",
          ),
        );
      } finally {
        setIsSigningId(null);
      }
    },
    [
      acceptedTermIds,
      applicationIdValid,
      isSubmitting,
      loadAgreementData,
      numericApplicationId,
      signatureName,
    ],
  );

  const completeApplication = useCallback(async () => {
    if (!applicationIdValid || isSubmitting) return;

    try {
      setErrorMessage(null);
      setStatusMessage('');
      setIsSubmitting(true);

      const progressResponse = await apiUpdateFdApplicationProgress({
        applicationId: numericApplicationId,
        completed: true,
        progress: AGREEMENT_PROGRESS_STEP,
        subProgress: 0,
        state: EApplicationStateType.PENDING_REVIEW,
      });

      setApplication((current) => {
        if (!current) return current;

        return {
          ...current,
          progress: progressResponse?.progress ?? AGREEMENT_PROGRESS_STEP,
          completed: progressResponse?.completed ?? true,
          subProgress: progressResponse?.subProgress ?? 0,
          applicationState:
            progressResponse?.applicationState ??
            EApplicationStateType.PENDING_REVIEW,
        };
      });

      await refreshJourney();
      history.push(lshGetRedirectPath(origin));
    } catch (error: any) {
      const message = lshGetErrorMessage(
        error,
        "We couldn't submit your application. Please try again.",
      );
      setErrorMessage(message);
      throw new Error(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    applicationIdValid,
    history,
    isSubmitting,
    numericApplicationId,
    origin,
    refreshJourney,
    setApplication,
  ]);

  const handleSubmitApplication = useCallback(async () => {
    if (!applicationIdValid || isSubmitting) return;

    if (!allTermsSigned) {
      setErrorMessage(
        'Sign all agreements before submitting your application.',
      );
      return;
    }

    setErrorMessage(null);
    setCashflowDialogOpen(true);
  }, [allTermsSigned, applicationIdValid, isSubmitting]);

  return (
    <ApplicationJourneyStepLayout
      title="Agreements & submission"
      description="Review every agreement, sign each term, then submit your completed application for Khula review."
      secondaryAction={{
        label: 'Back',
        onClick: goPrevious,
        disabled: isSubmitting,
      }}
      primaryAction={{
        label: isSubmitting ? 'Completing...' : 'Complete application',
        onClick: () => {
          void handleSubmitApplication();
        },
        loading: isSubmitting,
        disabled: isLoading || isSubmitting || !allTermsSigned,
      }}
    >
      <div className="agreement-step agreement-step--source">
        <div className="sr-only" role="status" aria-live="polite">
          {statusMessage}
        </div>

        <ErrorBanner message={errorMessage} />

        {!errorMessage ? <StatusMessage message={statusMessage} /> : null}

        <div className="application-card">
          <div className="subform-stack">
            <header className="subform-stack__header">
              <h3>Signature details</h3>
              <p>
                Enter the name that should appear on each agreement signature.
              </p>
            </header>

            <FormField
              as="div"
              label="Signature name"
              htmlFor="supplier-signature-name"
            >
              <TextInput
                id="supplier-signature-name"
                value={signatureName}
                onChange={(event) => setSignatureName(event.target.value)}
                placeholder="Type full legal name"
                disabled={isLoading || isSubmitting}
              />
            </FormField>

            {isLoading ? (
              <div className="application-empty-state">
                <h3>Loading agreements</h3>
              </div>
            ) : terms.length === 0 ? (
              <div className="application-empty-state">
                <h3>No agreements available</h3>
                <p>
                  Agreement terms are not available for this application yet.
                </p>
              </div>
            ) : (
              <ul className="agreement-list">
                {terms.map((term) => {
                  const termId = Number(term.id);
                  const isSigned = signedTermIds.has(termId);
                  const isAccepted = acceptedTermIds.includes(termId);
                  const isSigning = isSigningId === termId;

                  return (
                    <li
                      key={term.id}
                      className={`agreement-list__item${
                        isSigned ? ' is-signed' : ''
                      }`}
                    >
                      <div className="agreement-list__header">
                        <h4>{term.agreementType}</h4>
                        <span>{isSigned ? 'Signed' : 'Pending'}</span>
                      </div>

                      <p>{term.agreement}</p>

                      {!isSigned ? (
                        <>
                          <label className="agreement-list__accept">
                            <input
                              type="checkbox"
                              checked={isAccepted}
                              onChange={() => toggleAccepted(termId)}
                              disabled={isSigning || isSubmitting}
                            />
                            I have read and accept this agreement.
                          </label>

                          <button
                            type="button"
                            className="money-btn money-btn--secondary money-btn--sm"
                            onClick={() => {
                              void handleSignAgreement(term);
                            }}
                            disabled={isSigning || isSubmitting}
                          >
                            {isSigning ? 'Signing...' : 'Sign agreement'}
                          </button>
                        </>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      {currentApplication ? (
        <ApplicationApprovedCashflowReviewDialog
          application={currentApplication}
          open={cashflowDialogOpen}
          confirmLabel={
            isSubmitting ? 'Completing...' : 'Create cashflow & complete'
          }
          onClose={() => {
            if (!isSubmitting) setCashflowDialogOpen(false);
          }}
          onPublished={async () => {
            await completeApplication();
          }}
        />
      ) : null}
    </ApplicationJourneyStepLayout>
  );
};

export default ApplicationAgreementStep;
